import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { TranslateLoader } from '@ngx-translate/core';
import { md5 } from 'js-md5';

import { I18N_CONFIG } from '../tokens/i18n-config.token';
import { LanguageStateService } from '../services/language-state.service';
import { ApiSource } from './sources/api.source';
import { CrowdlSource } from './sources/crowdl.source';
import { StorageService } from '../services/storage.service';
import { EnvironmentType } from '../interfaces';

// CD-3227: the dashboard TranslateLoader (this class, wired in i18n.providers.ts)
// must hit the SAME per-env Cloudflare R2 lang hosts as CustomTranslateHttpLoader.
// The shared nginx `lang.coindepo.io` returns no Access-Control-Allow-Origin, so
// `dev-app.coindepo.io` was CORS-blocked. Each env hits its own R2 bucket host
// whose CORS policy scopes to that env's app origins.
const LANG_EDGE_HOSTS: Record<EnvironmentType, string> = {
  dev: 'https://dev-lang.coindepo.io',
  stage: 'https://stage-lang.coindepo.io',
  prod: 'https://lang.coindepo.com',
};

export class CustomTranslateLoader implements TranslateLoader {
  private http = inject(HttpClient);
  private config = inject(I18N_CONFIG);
  private state = inject(LanguageStateService);
  private storage = inject(StorageService);

  getTranslation(lang: string): Observable<any> {
    const sourceType = this.storage.get('translate-source');

    let source;

    if (sourceType === 'crowdl') {
      const timestamp = Date.now().toString();
      const hash = this.getHash(timestamp);
      source = new CrowdlSource(this.http, {
        projectId: this.config.crowdinId,
        apiKey: 'tkLEF5rdayPUxR7PkkVfzqWgWE97PUA8',
        secret: hash,
      });
    } else {
      // Defensive default only — a valid EnvironmentType always hits the map.
      // Fall back to PROD (never dev): a mis-set `env` must not route prod users
      // to the dev bucket.
      const host = LANG_EDGE_HOSTS[this.config.env] ?? LANG_EDGE_HOSTS.prod;

      source = new ApiSource(this.http, host, this.config.type);
    }

    return source.load(lang).pipe(
      tap(() => this.state.setLoaded())
    );
  }

  getHash(timestamp: string): string {
    const secret = 'FWkbZnLo1oIH3KEw0CnmQ22hvn56o3vs';
    return md5(timestamp + secret);
  }
}
